import { pageSxFor } from '../components/pageLayout';
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import { Search } from "@mui/icons-material";
import { AccountTree, CellTower, Flag, Shield } from '@mui/icons-material';  // same icons as the unit dialogs (units/Add.js)

import Header from '../components/Header'
import PageBanner from '../components/PageBanner';
import FlexBetween from '../components/FlexBetween';
import LoadingOverlay from '../components/LoadingOverlay';
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { useAuthContext } from '../context/auth'
import { usePathContext } from '../context/path'
import { ROLES } from '../config/roles'
import { useUnitContext } from '../context/unit';
import Add from '../components/units/Add';
import Edit from '../components/units/Edit';
import Delete from '../components/units/Delete';
import Reset from '../components/units/Reset';
import useGridStyles from '../hooks/useGridStyles';
import OnlineStatusChip from '../components/OnlineStatusChip';
import OnlineCountPills from '../components/OnlineCountPills';

const Unit = () => {
    // theme
    const theme = useTheme();
    const { gridSx, gridBorder } = useGridStyles();
    // is large desktop screen
    const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");

    const { auth } = useAuthContext()
    const { setTitle } = usePathContext()
    const { units, dispatch } = useUnitContext()
    const [ query, setQuery ] = useState("")
    const [ notFound, setNotFound ] = useState(false)
    const axiosPrivate = useAxiosPrivate()
    const isAdminOrRoot = auth.roles.includes(ROLES.Admin) || auth.roles.includes(ROLES.Root)
    const admin = auth && isAdminOrRoot

    const columns0 = [
        {
            field: "order",
            headerName: "No.",
            flex: 0.2,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (!params.api || !params.id) return null;
                const index = params.api.getAllRowIds?.().indexOf(params.id);
                return <span>{index + 1}</span>;
            }
        },
        {
            field: "name",
            headerName: "Name",
            flex: 0.5
        },
        {
            field: "parentId",
            headerName: "Parent Unit",
            flex: 0.5,
            valueGetter: (params) => {
                const unitId = params;
                const matchedUnit = units.find((unit) => unit._id === unitId);
                return matchedUnit ? matchedUnit.name : "*";
            },
        },
        {
            field: "actions",
            headerName: "Action",
            type: "actions",
            width: 80,
            renderCell: (params) => (
                <div>
                    <Edit 
                        id={params.row._id} 
                        type={ params.row.type }
                        name={ params.row.name }
                        parentId={ params.row.parentId }
                        units={units}
                    />
                    <Delete id={params.row._id} name={ params.row.name } type={ params.row.type } />
                </div>                
            ),
        }
    ];

    const columns = [
        {
            field: "order",
            headerName: "No.",
            flex: 0.2,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (!params.api || !params.id) return null;
                const index = params.api.getAllRowIds?.().indexOf(params.id);
                return <span>{index + 1}</span>;
            }
        },
        {
            field: "name",
            headerName: "Name",
            flex: 0.5
        },
        {
            field: "parentId",
            headerName: "Parent Unit",
            flex: 0.5,
            valueGetter: (params) => {
                const unitId = params;
                const matchedUnit = units.find((unit) => unit._id === unitId);
                return matchedUnit ? matchedUnit.name : "*";
            },
        },
        {
            field: "number",
            headerName: "Number",
            flex: 0.3
        },
        {
            field: "latitude",
            headerName: "Latitude",
            flex: 0.5
        },
        {
            field: "longitude",
            headerName: "Longitude",
            flex: 0.5
        },
        {
            field: "accuracy",
            headerName: "Accuracy",
            flex: 0.3,
        },
        {
            field: "trueNorth",
            headerName: "TrueNorth",
            flex: 0.3,
        },
        {
            field: "distanceRate",
            headerName: "DistanceRate",
            flex: 0.4,
        },
        {
            field: "isOnline",
            headerName: "Online",
            flex: 0.4,
            minWidth: 110,
            renderCell: (params) => {
                const isOnline = params?.row?.isOnline === true;
                return (
                    <OnlineStatusChip
                        online={isOnline}
                        tip={isOnline ? 'Outpost is connected' : 'Outpost is not connected'}
                    />
                );
            },
        },
        {
            field: "actions",
            headerName: "Action",
            type: "actions",
            width: 80,
            renderCell: (params) => (
                <div>
                    <Edit 
                        id={params.row._id} 
                        type={ params.row.type }
                        name={ params.row.name }
                        parentId={ params.row.parentId }
                        number={ params.row.number } 
                        latitude={ params.row.latitude } 
                        longitude={ params.row.longitude } 
                        accuracy={ params.row.accuracy }
                        trueNorth={ params.row.trueNorth }
                        distanceRate={ params.row.distanceRate }
                        units={units}
                    />
                    <Delete id={params.row._id} name={ params.row.name } type={ params.row.type } />
                </div>                
            ),
        }
    ];
    
    useEffect(() => {
        setTitle("Unit Management")
        const abortController = new AbortController()
    
        const getAllUnit = async () => {
          try {
            const response = await axiosPrivate.get('/api/units', {
              signal: abortController.signal
            })
            dispatch({type: 'SET_UNIT', payload: response.data})
            setNotFound(false)
          } catch (err) {
            // console.log(err)
            setNotFound(true)
          }
        }
        
        if(auth){
            getAllUnit()
        }

        return () => {
            abortController.abort()
          }
    },[auth, dispatch, axiosPrivate, setTitle])

    const filteredUnits = useMemo(() => 
        [...(units || [])]
            .filter(unit =>
                unit.name && unit.name.toLowerCase().includes(query.toLowerCase())
            )
    );

    const commandCount = useMemo(() => {
        return filteredUnits?.filter((unit) => unit.type === 0).length || 0
    }, [filteredUnits])

    const regimentCount = useMemo(() => {
        return filteredUnits?.filter((unit) => unit.type === 1).length || 0
    }, [filteredUnits])

    const companyCount = useMemo(() => {
        return filteredUnits?.filter((unit) => unit.type === 2).length || 0
    }, [filteredUnits])

    const outpostCount = useMemo(() => {
        return filteredUnits?.filter((unit) => unit.type === 3).length || 0
    }, [filteredUnits])

    const outpostOnlineCount = useMemo(() => {
        return filteredUnits?.filter((unit) => unit.type === 3 && unit.isOnline === true).length || 0
    }, [filteredUnits])

    // Left column row heights follow each panel's row count, so a panel with many units gets
    // more space and a near-empty one shrinks. Each track is at least the panel + grid header
    // (~130px) plus room for 2 rows, so items are never hidden behind a collapsed panel.
    const LEFT_PANEL_CHROME = 180;
    const LEFT_ROW_HEIGHT = 52;
    const leftMins = [commandCount, regimentCount, companyCount]
        .map((count) => LEFT_PANEL_CHROME + LEFT_ROW_HEIGHT * Math.min(Math.max(count, 1), 2));
    const leftRows = [commandCount, regimentCount, companyCount]
        .map((count, i) => `minmax(${leftMins[i]}px, ${count + 3}fr)`)
        .join(" ");
    // grid can't shrink below the sum of its minimums (+ 2 gaps of 20px); the page scrolls then
    const leftMinHeight = leftMins.reduce((a, b) => a + b, 0) + 40;

    return (
        <>
            {admin && (
                <>
                    <Box sx={pageSxFor(isNonMediumScreen)}>
                        <FlexBetween 
                            sx={{
                                flexDirection: {
                                    xs: 'column',
                                    sm: 'row',
                                },
                                    alignItems: {
                                    xs: 'flex-start',
                                    sm: 'center',
                                },
                                gap: '1rem',
                            }}
                        >
                            {/* Header */}
                            <Header title="UNITS" subtitle="List Units and Add / Remove Unit" />
                            <PageBanner />

                            <FlexBetween>       
                                {/* Search */}
                                <TextField
                                    label="Search by Unit Name"
                                    placeholder="Type to search..."
                                    variant="standard"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    sx={{
                                        mb: "0.5rem",
                                        mr: "1rem",
                                        width: "15rem",
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                        <InputAdornment position="end">
                                            <Search sx={{ color: theme.palette.secondary[200] }} />
                                        </InputAdornment>
                                        ),
                                    }}
                                />
                            </FlexBetween>
                        </FlexBetween>

                        <Box
                            mt="10px"
                            display="grid"
                            gridTemplateColumns="repeat(6, 1fr)"
                            gap="20px"
                            sx={{
                                // Wide screens: the 3 left rows share the height under the header
                                // in proportion to their row counts (page scrolls only below 160px a row);
                                // narrow: 360px rows
                                flex: isNonMediumScreen ? 1 : "none",
                                // shrink to the page height instead of growing with the tables,
                                // but never below what the left panels need
                                minHeight: isNonMediumScreen ? `${leftMinHeight}px` : undefined,
                                gridTemplateRows: isNonMediumScreen ? leftRows : "none",
                                gridAutoRows: isNonMediumScreen ? undefined : "360px",
                                "& > div": {
                                    gridColumn: isNonMediumScreen ? undefined : "span 12",
                                },
                            }}
                        >
                            <Box
                                gridColumn="span 2"
                                gridRow="span 1"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                p="1.25rem 1rem"
                                flex="1 1 100%"
                                backgroundColor={theme.palette.background.alt}
                                border={gridBorder}
                                borderRadius="0.55rem"
                                sx={{
                                    padding: "0px",
                                    minHeight: 0,
                                }}
                                overflow="hidden"
                            >  
                                <FlexBetween>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ padding: "10px 10px" }}>
                                        <Shield sx={{ color: theme.palette.secondary[200] }} />
                                        <Typography variant="h4" sx={{ color: theme.palette.secondary[200] }}>
                                            Commands
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: theme.palette.secondary[100] }}>
                                            ({commandCount})
                                        </Typography>
                                    </Box>

                                    <Add type={0} units={units}/>                                 
                                </FlexBetween>                                
                                <Box
                                    flex={1}
                                    minHeight={0}
                                >
                                    <DataGrid
                                        loading={!filteredUnits}
                                        getRowId={(row) => row._id}
                                        rows={(filteredUnits && filteredUnits.filter(unit => unit.type === 0)) || []}
                                        columns={columns0}
                                        hideFooter
                                        disableRowSelectionOnClick
                                        components={{
                                            ColumnMenu: CustomColumnMenu,
                                        }}
                                        sx={gridSx}
                                    />
                                </Box>                    
                            </Box>    

                            <Box
                                gridColumn="span 4"
                                gridRow="span 3"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                p="1.25rem 1rem"
                                flex="1 1 100%"
                                backgroundColor={theme.palette.background.alt}
                                border={gridBorder}
                                borderRadius="0.55rem"
                                sx={{
                                    padding: "0px",
                                    minHeight: 0,
                                }}
                                overflow="hidden"
                            >  
                                <FlexBetween>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ padding: "10px 10px" }}>
                                        <CellTower sx={{ color: theme.palette.secondary[200] }} />
                                        <Typography variant="h4" sx={{ color: theme.palette.secondary[200], mr: 1 }}>
                                            Outposts
                                        </Typography>
                                        <OnlineCountPills
                                            size="small"
                                            total={outpostCount}
                                            online={outpostOnlineCount}
                                            totalIcon={<CellTower sx={{ fontSize: 16, color: theme.palette.secondary.main }} />}
                                        />
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={4} >
                                        <Reset units={units}/>
                                        <Add type={3} units={units}/> 
                                    </Box>                                    
                                </FlexBetween> 
                                <Box
                                    flex={1}
                                    minHeight={0}
                                >
                                    <DataGrid
                                        loading={!filteredUnits}
                                        getRowId={(row) => row._id}
                                        rows={
                                            filteredUnits
                                                ? filteredUnits
                                                    .filter(unit => unit.type === 3)
                                                    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))   // Sort by number
                                                : []
                                        }
                                        columns={columns}
                                        disableRowSelectionOnClick
                                        components={{
                                            ColumnMenu: CustomColumnMenu,
                                        }}
                                        sx={gridSx}
                                    />
                                </Box>
                            </Box>

                            <Box
                                gridColumn="span 2"
                                gridRow="span 1"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                p="1.25rem 1rem"
                                flex="1 1 100%"
                                backgroundColor={theme.palette.background.alt}
                                border={gridBorder}
                                borderRadius="0.55rem"
                                sx={{
                                    padding: "0px",
                                    minHeight: 0,
                                }}
                                overflow="hidden"
                            >  
                                <FlexBetween>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ padding: "10px 10px" }}>
                                        <AccountTree sx={{ color: theme.palette.secondary[200] }} />
                                        <Typography variant="h4" sx={{ color: theme.palette.secondary[200] }}>
                                            Regiments
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: theme.palette.secondary[100] }}>
                                            ({regimentCount})
                                        </Typography>
                                    </Box>

                                    <Add type={1} units={units}/> 
                                </FlexBetween> 
                                <Box
                                    flex={1}
                                    minHeight={0}
                                >
                                    <DataGrid
                                        loading={!filteredUnits}
                                        getRowId={(row) => row._id}
                                        rows={(filteredUnits && filteredUnits.filter(unit => unit.type === 1)) || []}
                                        columns={columns0}
                                        hideFooter
                                        disableRowSelectionOnClick
                                        components={{
                                            ColumnMenu: CustomColumnMenu,
                                        }}
                                        sx={gridSx}
                                    />
                                </Box>
                            </Box>

                            <Box
                                gridColumn="span 2"
                                gridRow="span 1"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                p="1.25rem 1rem"
                                flex="1 1 100%"
                                backgroundColor={theme.palette.background.alt}
                                border={gridBorder}
                                borderRadius="0.55rem"
                                sx={{
                                    padding: "0px",
                                    minHeight: 0,
                                }}
                                overflow="hidden"
                            >
                                <FlexBetween>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ padding: "10px 10px" }}>
                                        <Flag sx={{ color: theme.palette.secondary[200] }} />
                                        <Typography variant="h4" sx={{ color: theme.palette.secondary[200] }}>
                                            Companies
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: theme.palette.secondary[100] }}>
                                            ({companyCount})
                                        </Typography>
                                    </Box>

                                    <Add type={2} units={units}/> 
                                </FlexBetween> 
                                <Box
                                    flex={1}
                                    minHeight={0}
                                >
                                    <DataGrid
                                        loading={!filteredUnits}
                                        getRowId={(row) => row._id}
                                        rows={(filteredUnits && filteredUnits.filter(unit => unit.type === 2)) || []}
                                        columns={columns0}
                                        hideFooter
                                        disableRowSelectionOnClick
                                        components={{
                                            ColumnMenu: CustomColumnMenu,
                                        }}
                                        sx={gridSx}
                                    />
                                </Box>
                            </Box>
                            
                        </Box>

                        {/* <LoadingOverlay isLoading={isSaving} showContent='Saving...' /> */}
                    </Box>   
                </> 
            )}

            {!filteredUnits?.length && query && <div>No matching results found...</div>}

            {notFound && !query && !units?.length && (<div>No Record Found...</div>)}
        </>
    )
}

export default Unit